import type { BilletSecurityComponent } from "../../types";

/**
 * Security-elements grid for the banknote ("billet") detail pages.
 *
 * Rendered below the recto/verso photos: each numbered marker baked onto the
 * banknote image maps to a card here (same number), so a reader can cross-check
 * "what is element 9 on the note?". Content is authored manually per denomination
 * — the feature set and wording differ between coupures (12 elements on the
 * 50–500 FC notes, 10 on the 1 000–20 000 FC notes; STEP vs Pastille
 * holographique; Okapi vs léopard watermark; etc.), so nothing is derived.
 */
export function BilletSecurityRenderer({ component }: { component: BilletSecurityComponent }) {
  const { title, features = [] } = component.content ?? ({} as never);
  if (!features.length) return null;

  return (
    <section>
      {title ? (
        <h2 className="text-lg sm:text-xl font-semibold text-[#0F2849]">{title}</h2>
      ) : null}
      <div className={`grid gap-4 sm:gap-5 sm:grid-cols-2${title ? " mt-4 sm:mt-5" : ""}`}>
        {features.map((f, idx) => (
          <div
            key={`${f.number}-${idx}`}
            className="flex gap-3.5 rounded-lg border border-slate-200 bg-white p-4 shadow-sm sm:p-5"
          >
            <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-[#0095DB] text-sm font-semibold text-[#0095DB]">
              {f.number}
            </span>
            <div className="min-w-0">
              <h3 className="text-sm font-semibold text-[#005CA9] sm:text-base">{f.title}</h3>
              <p className="mt-1 text-sm leading-6 text-slate-600">{f.description}</p>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
